import React, { memo, useState, useCallback, useEffect, useMemo, useRef } from "react"
import PropTypes from "prop-types"
import { Col, Flex, Row, Switch, Spin } from "antd"
import { Text } from "ui/Typography"
import { Icon } from "ui/Icon"
import { colors } from "themes/variables"
import {
  filterPermissionsByColumn,
  transformPermissionHashToUI,
  getInitialExpandedFromPermissions,
  getInitialSelectedTabsFromPermissions,
  buildAssignPermissionPayload
} from "../utils/rolesAccessHelpers"
import styles from "../index.module.css"

const SubPermission = memo(({ subPerm, selectedTabs, onTabChange, onToggle, isAnyUpdateInProgress, styles }) => {
  return (
    <Flex gap={12} align="center" justify="space-between" className={styles.accessItem} wrap>
      <div className={`${styles.subPermissionItem} ${isAnyUpdateInProgress ? styles.subPermissionItemDisabled : ""}`}>
        <Switch
          checked={subPerm.enabled}
          onChange={(checked) => !isAnyUpdateInProgress && onToggle(subPerm, checked)}
          size="small"
          className={isAnyUpdateInProgress ? styles.subPermissionSwitch : ""}
        />
        <Text className={styles.accessLabel}>{subPerm.label}</Text>
      </div>
      {subPerm?.options && subPerm?.enabled && (
        <div className={`${styles.tabGroup} ${isAnyUpdateInProgress ? styles.tabGroupDisabled : ""}`}>
          {subPerm?.options?.map((option) => {
            const isActive = (selectedTabs[subPerm.id] || subPerm?.selectedScope || subPerm?.options?.[0]) === option
            return (
              <button
                key={option}
                className={`${styles.tabButton} ${isActive ? styles.tabButtonActive : ""} ${isAnyUpdateInProgress ? styles.tabButtonDisabled : ""
                  }`}
                onClick={() => !isAnyUpdateInProgress && onTabChange(subPerm.id, option)}
              >
                <Text color={isActive ? "var(--color-primary)" : colors?.textSecondaryDark} className={styles.tabButtonText}>
                  {option}
                </Text>
              </button>
            )
          })}
        </div>
      )}
    </Flex>
  )
})

SubPermission.displayName = "SubPermission"

SubPermission.propTypes = {
  subPerm: PropTypes.object.isRequired,
  selectedTabs: PropTypes.object.isRequired,
  onTabChange: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
  isAnyUpdateInProgress: PropTypes.bool,
  styles: PropTypes.object.isRequired
}

const PermissionGroup = memo(
  ({
    permission,
    isExpanded,
    onToggle,
    selectedTabs,
    onTabChange,
    onSubPermissionToggle,
    onFullAccessToggle,
    updatingPermissions,
    isAnyUpdateInProgress,
    styles
  }) => {
    const isModuleUpdating = updatingPermissions[permission.id]

    return (
      <div className={`${styles.accessGroup} ${styles.tabGroupRelative}`}>
        <div className={`${styles.accessHeader} ${isAnyUpdateInProgress && !isModuleUpdating ? styles.accessHeaderDisabled : ""}`}>
          <div className={styles.accessIcon}>
            <Icon type={permission.iconType || "dsicon"} name={permission.iconName} width={20} height={20} color={colors.textSecondary} />
          </div>
          <Text className={styles.accessTitle}>{permission.title}</Text>
          <Switch
            checked={permission.hasOnlyFullAccess ? permission.isEnabled : isExpanded}
            onChange={() => {
              if (isAnyUpdateInProgress && !isModuleUpdating) return
              permission.hasOnlyFullAccess ? onFullAccessToggle(permission) : onToggle(permission.id)
            }}
            className={`${styles.accessSwitch} ${isAnyUpdateInProgress && !isModuleUpdating ? styles.accessSwitchDisabled : ""}`}
            loading={permission.hasOnlyFullAccess && isModuleUpdating}
          />
        </div>
        {!permission.hasOnlyFullAccess && isExpanded && permission?.subPermissions?.length > 0 && (
          <div className={styles.accessDetails}>
            {permission?.subPermissions?.map((subPerm) => (
              <SubPermission
                key={subPerm.id}
                subPerm={subPerm}
                selectedTabs={selectedTabs}
                onTabChange={onTabChange}
                onToggle={(subPerm, checked) => onSubPermissionToggle(permission, subPerm, checked)}
                isAnyUpdateInProgress={isAnyUpdateInProgress}
                styles={styles}
              />
            ))}
          </div>
        )}
        {isModuleUpdating && (
          <div className={styles.tabLoadingOverlay}>
            <Spin size="small" />
          </div>
        )}
      </div>
    )
  }
)

PermissionGroup.displayName = "PermissionGroup"

PermissionGroup.propTypes = {
  permission: PropTypes.object.isRequired,
  isExpanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  selectedTabs: PropTypes.object.isRequired,
  onTabChange: PropTypes.func.isRequired,
  onSubPermissionToggle: PropTypes.func.isRequired,
  onFullAccessToggle: PropTypes.func.isRequired,
  updatingPermissions: PropTypes.object,
  isAnyUpdateInProgress: PropTypes.bool,
  styles: PropTypes.object.isRequired
}

const PermissionsPanel = ({ selectedRole, t, onPermissionChange }) => {
  const [expandedPermissions, setExpandedPermissions] = useState({})
  const [selectedTabs, setSelectedTabs] = useState({})
  const [updatingPermissions, setUpdatingPermissions] = useState({})
  const [isAnyUpdateInProgress, setIsAnyUpdateInProgress] = useState(false)
  const updatingPermissionsRef = useRef({})

  const dynamicPermissionsData = useMemo(() => {
    if (selectedRole?.permission_hash) {
      return transformPermissionHashToUI(selectedRole.permission_hash, t)
    }
    return []
  }, [selectedRole, t])

  useEffect(() => {
    if (selectedRole?.permission_hash) {
      const initialExpanded = getInitialExpandedFromPermissions(selectedRole?.permission_hash)
      const initialTabs = getInitialSelectedTabsFromPermissions(selectedRole?.permission_hash)
      setExpandedPermissions(initialExpanded)
      setSelectedTabs(initialTabs)
    } else {
      setExpandedPermissions({})
      setSelectedTabs({})
    }
  }, [selectedRole])

  const togglePermission = useCallback((permission) => {
    setExpandedPermissions((prev) => ({
      ...prev,
      [permission]: !prev[permission]
    }))
  }, [])

  const handleSubPermissionToggle = useCallback(
    async (permission, subPerm, isEnabled) => {
      if (!onPermissionChange || !selectedRole) return

      if (updatingPermissionsRef.current[permission.id]) {
        return
      }

      updatingPermissionsRef.current = { ...updatingPermissionsRef.current, [permission.id]: true }
      setUpdatingPermissions((prev) => ({ ...prev, [permission.id]: true }))
      setIsAnyUpdateInProgress(true)

      try {
        const modules = Array.isArray(selectedRole?.permission_hash)
          ? selectedRole.permission_hash
          : Object.values(selectedRole?.permission_hash || {})
        const originalModule = modules.find((m) => m?.access_module_id === permission?.access_module_id)

        if (!originalModule) {
          setUpdatingPermissions((prev) => ({ ...prev, [permission.id]: false }))
          setIsAnyUpdateInProgress(false)
          return
        }

        const scopeValue = isEnabled && subPerm.options && subPerm.options.length > 0
          ? (() => {
            const scopeLabel = selectedTabs[subPerm.id] || subPerm.selectedScope || subPerm.options[0]
            const screenKey = subPerm.screenKey || Object.keys(originalModule.available_options || {})[0]
            const actions = originalModule.available_options?.[screenKey]?.actions
            const scopes = actions?.[subPerm.key]?.scopes || originalModule.available_options?.[subPerm.key]?.scope
            const scopeItem = scopes?.find((s) => s.label === scopeLabel)
            return scopeItem?.value || scopeLabel
          })()
          : null

        const payload = buildAssignPermissionPayload(originalModule.access_module_id, subPerm, isEnabled, scopeValue)

        await onPermissionChange(selectedRole.id, payload)
      } finally {
        updatingPermissionsRef.current = { ...updatingPermissionsRef.current, [permission.id]: false }
        setUpdatingPermissions((prev) => ({ ...prev, [permission.id]: false }))
        setIsAnyUpdateInProgress(false)
      }
    },
    [onPermissionChange, selectedRole, selectedTabs]
  )

  const handleTabChange = useCallback(
    async (permissionId, option) => {
      setSelectedTabs((prev) => ({
        ...prev,
        [permissionId]: option
      }))

      if (!onPermissionChange || !selectedRole) return

      const [moduleId] = permissionId.split("-")
      const permission = dynamicPermissionsData.find((p) => p.id === moduleId)
      const subPerm = permission?.subPermissions.find((sp) => sp.id === permissionId)

      if (!permission || !subPerm) return

      if (updatingPermissionsRef.current[permission.id]) {
        return
      }

      const modules = Array.isArray(selectedRole?.permission_hash)
        ? selectedRole.permission_hash
        : Object.values(selectedRole?.permission_hash || {})
      const originalModule = modules.find((m) => m.access_module_id === permission.access_module_id)

      if (!originalModule) return

      let scopeApiValue = null
      const screenKey = subPerm.screenKey || Object.keys(originalModule.available_options || {})[0]
      const scopes = originalModule.available_options?.[screenKey]?.actions?.[subPerm.key]?.scopes || originalModule.available_options?.[subPerm.key]?.scope

      if (scopes) {
        const scopeItem = scopes.find((s) => s.label === option)
        scopeApiValue = scopeItem?.value
      }

      if (!scopeApiValue) return

      updatingPermissionsRef.current = { ...updatingPermissionsRef.current, [permission.id]: true }
      setUpdatingPermissions((prev) => ({ ...prev, [permission.id]: true }))
      setIsAnyUpdateInProgress(true)

      try {
        const payload = buildAssignPermissionPayload(originalModule.access_module_id, subPerm, true, scopeApiValue)

        await onPermissionChange(selectedRole.id, payload)
      } finally {
        updatingPermissionsRef.current = { ...updatingPermissionsRef.current, [permission.id]: false }
        setUpdatingPermissions((prev) => ({ ...prev, [permission.id]: false }))
        setIsAnyUpdateInProgress(false)
      }
    },
    [onPermissionChange, selectedRole, dynamicPermissionsData, selectedTabs]
  )

  const handleFullAccessToggle = useCallback(
    async (permission) => {
      if (!onPermissionChange || !selectedRole) return

      updatingPermissionsRef.current = { ...updatingPermissionsRef.current, [permission.id]: true }
      setUpdatingPermissions((prev) => ({ ...prev, [permission.id]: true }))
      setIsAnyUpdateInProgress(true)

      try {
        const modules = Array.isArray(selectedRole?.permission_hash)
          ? selectedRole.permission_hash
          : Object.values(selectedRole?.permission_hash || {})
        const originalModule = modules.find((m) => m.access_module_id === permission.access_module_id)

        if (!originalModule) {
          setUpdatingPermissions((prev) => ({ ...prev, [permission.id]: false }))
          setIsAnyUpdateInProgress(false)
          return
        }

        const fullAccessPerm = permission.subPermissions?.[0]
        const payload = {
          role: {
            permissions_attributes: [{
              id: fullAccessPerm?.permission_id || null,
              access_module_id: originalModule.access_module_id,
              access_screen_id: fullAccessPerm?.access_screen_id || null,
              action_name: "full_access",
              is_active: !permission.isEnabled
            }]
          }
        }

        await onPermissionChange(selectedRole.id, payload)
      } finally {
        updatingPermissionsRef.current = { ...updatingPermissionsRef.current, [permission.id]: false }
        setUpdatingPermissions((prev) => ({ ...prev, [permission.id]: false }))
        setIsAnyUpdateInProgress(false)
      }
    },
    [onPermissionChange, selectedRole]
  )

  const leftColumnPermissions = useMemo(() => filterPermissionsByColumn(dynamicPermissionsData, "left"), [dynamicPermissionsData])
  const rightColumnPermissions = useMemo(() => filterPermissionsByColumn(dynamicPermissionsData, "right"), [dynamicPermissionsData])

  if (!selectedRole) {
    return (
      <div className={styles.accessSection}>
        <div className={styles.emptyState}>
          <Text className={styles.emptyStateText}>{t("staff.roles_access.no_role_selected")}</Text>
        </div>
      </div>
    )
  }

  const hasPermissions = selectedRole.permission_hash && (
    Array.isArray(selectedRole.permission_hash)
      ? selectedRole.permission_hash.length > 0
      : Object.keys(selectedRole.permission_hash).length > 0
  )

  if (!hasPermissions) {
    return (
      <div className={styles.accessSection}>
        <Text className={styles.sectionTitle}>
          {t("staff.roles_access.access_for")} {selectedRole?.name || ""}
        </Text>
        <div className={styles.emptyState}>
          <Text className={styles.emptyStateText}>{t("staff.roles_access.no_permissions_for_role")}</Text>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.accessSection}>
      <Text className={styles.sectionTitle}>
        {t("staff.roles_access.access_for")} {selectedRole?.name || ""}
      </Text>
      <Row gutter={[12, 0]}>
        <Col xs={24} lg={24} xl={12}>
          {leftColumnPermissions.map((permission) => (
            <PermissionGroup
              key={permission.id}
              permission={permission}
              isExpanded={expandedPermissions[permission.id]}
              onToggle={togglePermission}
              selectedTabs={selectedTabs}
              onTabChange={handleTabChange}
              onSubPermissionToggle={handleSubPermissionToggle}
              onFullAccessToggle={handleFullAccessToggle}
              updatingPermissions={updatingPermissions}
              isAnyUpdateInProgress={isAnyUpdateInProgress}
              styles={styles}
            />
          ))}
        </Col>
        <Col xs={24} lg={24} xl={12}>
          {rightColumnPermissions.map((permission) => (
            <PermissionGroup
              key={permission.id}
              permission={permission}
              isExpanded={expandedPermissions[permission.id]}
              onToggle={togglePermission}
              selectedTabs={selectedTabs}
              onTabChange={handleTabChange}
              onSubPermissionToggle={handleSubPermissionToggle}
              onFullAccessToggle={handleFullAccessToggle}
              updatingPermissions={updatingPermissions}
              isAnyUpdateInProgress={isAnyUpdateInProgress}
              styles={styles}
            />
          ))}
        </Col>
      </Row>
    </div>
  )
}

PermissionsPanel.propTypes = {
  selectedRole: PropTypes.object,
  t: PropTypes.func.isRequired,
  onPermissionChange: PropTypes.func
}

export default memo(PermissionsPanel)
